import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as InputProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import EligibleAgentTemplateSelect from 'components/EligibleAgentTemplateSelect/EligibleAgentTemplateSelect';
import Modal from 'components/Modal/Modal';
import SidePanel from 'components/SidePanel/SidePanel';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { REPUTATION_MONITORING_AGENT } from 'constants/monitoringAgents';
import { getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import AICardPanelHeader from './components/AICardPanelHeader';
import AICardPanelSearchDropdown from './components/AICardPanelSearchDropdown';
import useAICardPanelSelectedUnitIds from './hooks/useAICardPanelSelectedUnitIds';
import useAICardPanelSession from './hooks/useAICardPanelSession';

import type IEligibleAgentTemplate from 'store/slices/valueChainMap/interfaces/IEligibleAgentTemplate';
import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';
import type IAICardPanel from './interfaces/IAICardPanel';

import styles from './sass/AICardPanel.module.scss';

type TNTierPanelStep = 'choose-type' | 'selection' | 'template-bom' | 'template-si';

const resolveInitialNTierStep = ({
  sessionId,
  agentConfigId,
  subType,
}: {
  sessionId?: number,
  agentConfigId?: number | null,
  subType?: string,
}): TNTierPanelStep => {
  if (!sessionId) {
    return 'choose-type';
  }

  if (agentConfigId == null) {
    if (subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE) {
      return 'template-si';
    }

    if (subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE) {
      return 'template-bom';
    }

    return 'choose-type';
  }

  return 'selection';
};

function AICardPanel({
  open,
  onClose,
  onBack,
  onAnalyzeBom,
  onSearchSuppliersSuccess,
  selectedCards,
  allAvailableCards,
  onSyncSelectedCardsFromSession,
  onRemoveSelectedCard,
  onToggleCard,
  onResetAll,
  sessionId,
  setSessionId,
  existingSession,
  isVCAgentSessionsLoaded,
  isVCAgentSessionsLoadFailed,
  isAIDraftUnitsReadyForSelectionSync,
  panelMode = 'n-tier',
  monitoringFlow = 'manual-session',
  monitoringAgentDefinition = REPUTATION_MONITORING_AGENT,
  onStartMonitoring,
  onContinueToSchedule,
  valueChainMapId = 0,
  onAgentConfigIdChange,
  onNTierSubTypeChange,
}: IAICardPanel) {
  const { t } = useTranslation();
  const isMonitoringPanel = panelMode === 'monitoring';
  const isScheduleFlow = isMonitoringPanel && monitoringFlow === 'new-schedule';
  const [nTierStep, setNTierStep] = useState<TNTierPanelStep>('choose-type');
  const [nTierChosenSubType, setNTierChosenSubType] = useState<string | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [pendingTemplateName, setPendingTemplateName] = useState<string | null>(null);
  const [monitoringAgentConfigId, setMonitoringAgentConfigId] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOpenLoaderVisible, setIsOpenLoaderVisible] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const hasInitializedOpenStateRef = useRef(false);
  const selectedUnitIds = useAICardPanelSelectedUnitIds({ selectedCards });
  const sessionCreateType = isMonitoringPanel
    ? monitoringAgentDefinition.sessionType
    : VALUE_CHAIN_AI_AGENT.SESSION_TYPE;
  const {
    search,
    setSearch,
    isResetConfirmOpen,
    setIsResetConfirmOpen,
    isSelectionHydrating,
    isSelectionSyncPending,
    isSearchSuppliersEnabled,
    isSelectionLocked,
    isBillOfMaterialsSession,
    isSupplierDiscoverySession,
    isStartVCAgentSessionLoading,
    isCreateVCAgentSessionLoading,
    handleResetAll,
    handleResetConfirm,
    handleSearchSuppliers,
    handleAnalyzeBom,
    handleStartMonitoring,
    confirmNTierCreateWithTemplate,
    confirmNTierStartWithTemplate,
    resolvedSession,
  } = useAICardPanelSession({
    open,
    onClose,
    onAnalyzeBom,
    onSearchSuppliersSuccess,
    selectedCards,
    onSyncSelectedCardsFromSession,
    onResetAll,
    sessionId,
    setSessionId,
    existingSession,
    isVCAgentSessionsLoaded,
    isVCAgentSessionsLoadFailed,
    isAIDraftUnitsReadyForSelectionSync,
    selectedUnitIds,
    onStartMonitoring,
    createAgentConfigId: isMonitoringPanel ? monitoringAgentConfigId : null,
    createAgentSubType: isMonitoringPanel ? monitoringAgentDefinition.subType : null,
    sessionCreateType,
    // N-tier creates on template confirm; schedule-only never creates a session.
    skipSessionCreate: !isMonitoringPanel || isScheduleFlow,
  });

  const sessionForChrome: ISessionVCItem | undefined = resolvedSession || existingSession;
  const isNTierTypeStep = !isMonitoringPanel && nTierStep === 'choose-type';
  const isNTierTemplateStep = !isMonitoringPanel
    && (nTierStep === 'template-bom' || nTierStep === 'template-si');
  const nTierTemplateUiAgentType = nTierStep === 'template-si'
    ? 'supplier-investigation'
    : 'bom';
  const nTierTemplateSubType = nTierChosenSubType
    || (nTierStep === 'template-si'
      ? VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
      : VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE);
  const isNTierConfirmPending = isStartVCAgentSessionLoading || isCreateVCAgentSessionLoading;
  const isUnitsUnlocked = isScheduleFlow || Boolean(sessionId);
  const isMonitoringTemplateOnly = isMonitoringPanel && !isScheduleFlow && !sessionId;
  const templateTitle = pendingTemplateName || getSessionTemplateTitle(sessionForChrome);

  const displayedSelectedCards = useMemo(
    () => (isSelectionHydrating ? [] : selectedCards),
    [isSelectionHydrating, selectedCards],
  );
  const isSelectedCardsLoaderVisible = isOpenLoaderVisible || isSelectionHydrating;
  const isSupplierSearchDisabledBySelection = selectedCards.some(
    (card) => card.type === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
  );

  const selectedNodeIds = useMemo(
    () => new Set(displayedSelectedCards.map((card) => card.nodeId)),
    [displayedSelectedCards],
  );

  const lockedSwimlane = displayedSelectedCards[0]?.swimlaneId;
  const lockedType = displayedSelectedCards[0]?.type;

  const dropdownItems = useMemo(() => {
    if (!search.trim()) return [];

    const searchLower = search.toLowerCase();

    return allAvailableCards.filter((card) => {
      if (isMonitoringPanel) {
        return card.name.toLowerCase().includes(searchLower);
      }

      const matchesSwimlane = !lockedSwimlane
        || (card.swimlaneId === lockedSwimlane && card.type === lockedType);

      return matchesSwimlane && card.name.toLowerCase().includes(searchLower);
    });
  }, [allAvailableCards, isMonitoringPanel, lockedSwimlane, lockedType, search]);

  const applyNTierSubType = useCallback((nextSubType: string | null) => {
    setNTierChosenSubType(nextSubType);
    onNTierSubTypeChange?.(nextSubType);
  }, [onNTierSubTypeChange]);

  const handleSearchWrapperFocus = useCallback(() => {
    if (search.trim()) {
      setIsDropdownOpen(true);
    }
  }, [search]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setIsDropdownOpen(value.trim().length > 0);
  }, [setSearch]);

  const handleDropdownToggle = useCallback((nodeId: string) => {
    if (!isUnitsUnlocked) {
      return;
    }
    onToggleCard(nodeId);
  }, [isUnitsUnlocked, onToggleCard]);

  const handleSearchBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!searchWrapperRef.current?.contains(e.relatedTarget as Node)) {
      setIsDropdownOpen(false);
    }
  }, []);

  // Loader must run once per open. sessionId / existingSession change after create
  // and must not cancel the 500ms timer (ATT-137 stuck spinner).
  useEffect(() => {
    if (!open) {
      hasInitializedOpenStateRef.current = false;
      setIsOpenLoaderVisible(false);
      setNTierStep('choose-type');
      applyNTierSubType(null);
      setPendingTemplateId(null);
      setPendingTemplateName(null);
      setMonitoringAgentConfigId(null);
      onAgentConfigIdChange?.(null);
      return undefined;
    }

    if (hasInitializedOpenStateRef.current) {
      return undefined;
    }

    hasInitializedOpenStateRef.current = true;
    setIsOpenLoaderVisible(true);
    const timeoutId = window.setTimeout(() => {
      setIsOpenLoaderVisible(false);
    }, 500);

    if (!isMonitoringPanel) {
      setNTierStep(resolveInitialNTierStep({
        sessionId,
        agentConfigId: existingSession?.agentConfigId,
        subType: existingSession?.subType,
      }));
      applyNTierSubType(existingSession?.subType ?? null);
      setPendingTemplateId(existingSession?.agentConfigId ?? null);
      setPendingTemplateName(getSessionTemplateTitle(existingSession) || null);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMonitoringPanel]);

  useEffect(() => {
    if (!open || !isMonitoringPanel || !existingSession?.agentConfigId) {
      return;
    }

    setMonitoringAgentConfigId(existingSession.agentConfigId);
    onAgentConfigIdChange?.(existingSession.agentConfigId);
    setPendingTemplateName(getSessionTemplateTitle(existingSession) || null);
  }, [
    existingSession,
    isMonitoringPanel,
    onAgentConfigIdChange,
    open,
  ]);

  const handleMonitoringAgentConfigChange = useCallback((
    nextId: number | null,
    template?: IEligibleAgentTemplate | null,
  ) => {
    setMonitoringAgentConfigId(nextId);
    setPendingTemplateName(template?.name?.trim() || null);
    onAgentConfigIdChange?.(nextId);
  }, [onAgentConfigIdChange]);

  const handlePanelBack = useCallback(() => {
    if (isNTierTemplateStep && !sessionId) {
      setNTierStep('choose-type');
      setPendingTemplateId(null);
      setPendingTemplateName(null);
      return;
    }
    onBack?.();
  }, [isNTierTemplateStep, onBack, sessionId]);

  const handleChooseNTierType = useCallback((subType: string) => {
    applyNTierSubType(subType);
    setPendingTemplateId(null);
    setPendingTemplateName(null);
    setNTierStep(
      subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
        ? 'template-si'
        : 'template-bom',
    );
  }, [applyNTierSubType]);

  const handleAnalyzeBomClick = useCallback(() => {
    if (isSelectionLocked && existingSession?.agentConfigId != null) {
      handleAnalyzeBom();
      return;
    }

    if (sessionId && pendingTemplateId) {
      confirmNTierStartWithTemplate({
        agentConfigId: pendingTemplateId,
        subType: VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE,
      }).catch(() => undefined);
      return;
    }

    setPendingTemplateId(
      existingSession?.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
        ? (existingSession.agentConfigId ?? null)
        : null,
    );
    applyNTierSubType(VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE);
    setNTierStep('template-bom');
  }, [
    applyNTierSubType,
    confirmNTierStartWithTemplate,
    existingSession?.agentConfigId,
    existingSession?.subType,
    handleAnalyzeBom,
    isSelectionLocked,
    pendingTemplateId,
    sessionId,
  ]);

  const handleSearchSuppliersClick = useCallback(() => {
    if (isSelectionLocked && existingSession?.agentConfigId != null) {
      handleSearchSuppliers(VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE);
      return;
    }

    if (sessionId && pendingTemplateId) {
      confirmNTierStartWithTemplate({
        agentConfigId: pendingTemplateId,
        subType: VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE,
      }).catch(() => undefined);
      return;
    }

    setPendingTemplateId(
      existingSession?.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
        ? (existingSession.agentConfigId ?? null)
        : null,
    );
    applyNTierSubType(VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE);
    setNTierStep('template-si');
  }, [
    applyNTierSubType,
    confirmNTierStartWithTemplate,
    existingSession?.agentConfigId,
    existingSession?.subType,
    handleSearchSuppliers,
    isSelectionLocked,
    pendingTemplateId,
    sessionId,
  ]);

  const handleConfirmNTierTemplate = useCallback(async () => {
    if (!pendingTemplateId) {
      return;
    }

    const didCreate = await confirmNTierCreateWithTemplate({
      agentConfigId: pendingTemplateId,
      subType: nTierTemplateSubType,
    });

    if (didCreate) {
      setNTierStep('selection');
    }
  }, [confirmNTierCreateWithTemplate, nTierTemplateSubType, pendingTemplateId]);

  const panelTitle = (() => {
    if (isNTierTypeStep) {
      return undefined;
    }

    if (isNTierTemplateStep || isMonitoringTemplateOnly) {
      return undefined;
    }

    return templateTitle || undefined;
  })();

  const panelTitleKey = (() => {
    if (panelTitle) {
      return undefined;
    }

    if (isMonitoringTemplateOnly) {
      return 'valueChainMap.agentTemplateStepTitleMonitoring';
    }

    if (isMonitoringPanel) {
      return monitoringAgentDefinition.selectionTitleKey;
    }

    if (nTierStep === 'choose-type') {
      return 'valueChainMap.agentTypeStepTitle';
    }

    if (nTierStep === 'template-bom') {
      return 'valueChainMap.agentTemplateBomStepTitle';
    }

    if (nTierStep === 'template-si') {
      return 'valueChainMap.agentTemplateSupplierStepTitle';
    }

    return undefined;
  })();

  const isBomLane = nTierChosenSubType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
    || isBillOfMaterialsSession;
  const isSiLane = nTierChosenSubType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
    || isSupplierDiscoverySession;

  const selectionFooter = (() => {
    if (isMonitoringPanel) {
      return (
        <Button
          title={t(isScheduleFlow
            ? monitoringAgentDefinition.continueToScheduleKey
            : monitoringAgentDefinition.startMonitoringKey)}
          labelKey={isScheduleFlow
            ? monitoringAgentDefinition.continueToScheduleKey
            : monitoringAgentDefinition.startMonitoringKey}
          variant={BUTTON_VARIANT.primary}
          onClick={isScheduleFlow ? onContinueToSchedule : handleStartMonitoring}
          className={styles.footerButton}
          disabled={
            !isUnitsUnlocked
            || !displayedSelectedCards.length
            || isSelectionHydrating
            || isSelectionSyncPending
            || !monitoringAgentConfigId
            || (isScheduleFlow && !onContinueToSchedule)
          }
        />
      );
    }

    const canStartBom = isBomLane && (
      Boolean(displayedSelectedCards.length)
      && !isSupplierDiscoverySession
      && isSearchSuppliersEnabled
      && !isSelectionHydrating
      && !isSelectionSyncPending
    );
    const canStartSi = isSiLane && (
      Boolean(displayedSelectedCards.length)
      && !isBillOfMaterialsSession
      && !isSupplierSearchDisabledBySelection
      && isSearchSuppliersEnabled
      && !isSelectionHydrating
      && !isSelectionSyncPending
    );

    if (isBomLane && !isSiLane) {
      return (
        <Button
          title={t('valueChainMap.aiPanelAnalyzeBom')}
          labelKey="valueChainMap.aiPanelAnalyzeBom"
          variant={BUTTON_VARIANT.primary}
          onClick={handleAnalyzeBomClick}
          className={styles.footerButton}
          disabled={!canStartBom}
          isLoading={isNTierConfirmPending}
        />
      );
    }

    if (isSiLane && !isBomLane) {
      return (
        <Button
          title={t('valueChainMap.aiPanelSearchSuppliers')}
          labelKey="valueChainMap.aiPanelSearchSuppliers"
          variant={BUTTON_VARIANT.primary}
          onClick={handleSearchSuppliersClick}
          className={styles.footerButton}
          disabled={!canStartSi}
          isLoading={isNTierConfirmPending}
        />
      );
    }

    return (
      <>
        <Button
          title={t('valueChainMap.aiPanelAnalyzeBom')}
          labelKey="valueChainMap.aiPanelAnalyzeBom"
          variant={BUTTON_VARIANT.primary}
          onClick={handleAnalyzeBomClick}
          className={styles.footerButton}
          disabled={!canStartBom}
        />
        <Button
          title={t('valueChainMap.aiPanelSearchSuppliers')}
          labelKey="valueChainMap.aiPanelSearchSuppliers"
          variant={BUTTON_VARIANT.primary}
          onClick={handleSearchSuppliersClick}
          className={styles.footerButton}
          disabled={!canStartSi}
        />
      </>
    );
  })();

  const renderUnitSelection = () => (
    <>
      {isMonitoringPanel && (isScheduleFlow || isMonitoringTemplateOnly) ? (
        <EligibleAgentTemplateSelect
          valueChainMapId={valueChainMapId}
          type={monitoringAgentDefinition.sessionType}
          subType={monitoringAgentDefinition.subType}
          value={monitoringAgentConfigId}
          onChange={(nextId, template) => handleMonitoringAgentConfigChange(nextId, template)}
          className={styles.templateSelect}
          disabled={isSelectionHydrating || Boolean(sessionId && !isScheduleFlow)}
        />
      ) : null}
      <div className={styles.headerSelected}>
        <div className={styles.headerSelectedLabel}>
          <span className={styles.selectedTitle}>
            {t('valueChainMap.aiPanelSelected')}
          </span>
          <span className={styles.selectedCounterValue}>{displayedSelectedCards.length}</span>
        </div>
        <button
          type="button"
          className={styles.resetAllButton}
          onClick={handleResetAll}
          disabled={!isUnitsUnlocked || !displayedSelectedCards.length || isSelectionHydrating}
        >
          {t('common.resetAll')}
        </button>
      </div>
      <div className={styles.infoBox}>
        <InfoIcon />
        <p className={styles.description}>
          {t(isMonitoringPanel
            ? monitoringAgentDefinition.selectionInfoKey
            : 'valueChainMap.aiPanelDescription')}
        </p>
      </div>
      <div
        ref={searchWrapperRef}
        className={styles.searchInputWrapper}
        onFocus={handleSearchWrapperFocus}
        onBlur={handleSearchBlur}
      >
        <TextField
          value={search}
          name="aiPanelSearch"
          placeholder={t('valueChainMap.aiPanelSearchPlaceholder')}
          icon={<SearchIcon />}
          className={styles.searchField}
          onChange={({ target }) => handleSearchChange(target.value)}
          disabled={!isUnitsUnlocked || isSelectionHydrating}
        />
        {isDropdownOpen && isUnitsUnlocked ? (
          <AICardPanelSearchDropdown
            items={dropdownItems}
            selectedNodeIds={selectedNodeIds}
            onToggle={handleDropdownToggle}
          />
        ) : null}
      </div>
      <div className={styles.selectedCardsList}>
        {isSelectedCardsLoaderVisible ? (
          <div className={styles.loaderOverlay}>
            <SmallLoader size={30} />
          </div>
        ) : null}
        {!isSelectedCardsLoaderVisible && displayedSelectedCards.map((card) => (
          <div key={card.nodeId} className={styles.selectedCardItem}>
            <InputProductIcon />
            <span className={styles.selectedCardName}>{card.name}</span>
            <button
              type="button"
              className={styles.selectedCardRemove}
              aria-label={t('common.delete')}
              onClick={() => onRemoveSelectedCard(card.nodeId)}
              disabled={!isUnitsUnlocked}
            >
              <CloseIcon />
            </button>
          </div>
        ))}
        {!isSelectedCardsLoaderVisible && !displayedSelectedCards.length ? (
          <div className={styles.emptyState}>
            {isMonitoringPanel
              ? t(monitoringAgentDefinition.selectionEmptyKey)
              : null}
          </div>
        ) : null}
      </div>
    </>
  );

  return (
    <>
      <SidePanel
        open={open}
        className={styles.container}
        ariaModal
        role="dialog"
        ariaLabel={t(isMonitoringPanel
          ? monitoringAgentDefinition.titleKey
          : 'valueChainMap.aiPanelTitle')}
      >
        <AICardPanelHeader
          onClose={onClose}
          onBack={isNTierTemplateStep || isNTierTypeStep || onBack ? handlePanelBack : undefined}
          titleKey={panelTitleKey}
          title={panelTitle}
        />

        <div className={styles.content}>
          {isNTierTypeStep ? (
            <div className={styles.templateStep}>
              <p className={styles.templateStepDescription}>
                {t('valueChainMap.agentTypeStepDescription')}
              </p>
            </div>
          ) : null}
          {isNTierTemplateStep || isMonitoringTemplateOnly ? (
            <div className={styles.templateStep}>
              <p className={styles.templateStepDescription}>
                {t(isMonitoringTemplateOnly
                  ? 'valueChainMap.agentTemplateStepDescriptionMonitoring'
                  : 'valueChainMap.agentTemplateStepDescription')}
              </p>
              {isNTierTemplateStep ? (
                <EligibleAgentTemplateSelect
                  valueChainMapId={valueChainMapId}
                  uiAgentType={nTierTemplateUiAgentType}
                  value={pendingTemplateId}
                  onChange={(nextId, template) => {
                    setPendingTemplateId(nextId);
                    setPendingTemplateName(template?.name?.trim() || null);
                  }}
                  label={nTierStep === 'template-si'
                    ? t('valueChainMap.agentTemplateSupplierLabel')
                    : t('valueChainMap.agentTemplateBomLabel')}
                  className={styles.templateSelect}
                  disabled={isNTierConfirmPending}
                />
              ) : (
                <EligibleAgentTemplateSelect
                  valueChainMapId={valueChainMapId}
                  type={monitoringAgentDefinition.sessionType}
                  subType={monitoringAgentDefinition.subType}
                  value={monitoringAgentConfigId}
                  onChange={(nextId, template) => handleMonitoringAgentConfigChange(nextId, template)}
                  className={styles.templateSelect}
                  disabled={isSelectionHydrating}
                />
              )}
            </div>
          ) : null}
          {!isNTierTypeStep && !isNTierTemplateStep && !isMonitoringTemplateOnly
            ? renderUnitSelection()
            : null}
        </div>

        <div className={styles.footer}>
          {isNTierTypeStep ? (
            <>
              <Button
                title={t('valueChainMap.aiPanelAnalyzeBom')}
                labelKey="valueChainMap.aiPanelAnalyzeBom"
                variant={BUTTON_VARIANT.primary}
                onClick={() => handleChooseNTierType(
                  VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE,
                )}
                className={styles.footerButton}
              />
              <Button
                title={t('valueChainMap.aiPanelSearchSuppliers')}
                labelKey="valueChainMap.aiPanelSearchSuppliers"
                variant={BUTTON_VARIANT.primary}
                onClick={() => handleChooseNTierType(
                  VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE,
                )}
                className={styles.footerButton}
              />
            </>
          ) : null}
          {isNTierTemplateStep ? (
            <Button
              title={t('valueChainMap.agentTemplateConfirmCreate')}
              variant={BUTTON_VARIANT.primary}
              onClick={() => { handleConfirmNTierTemplate().catch(() => undefined); }}
              className={styles.footerButtonFull}
              disabled={!pendingTemplateId || isNTierConfirmPending}
              isLoading={isNTierConfirmPending}
            />
          ) : null}
          {!isNTierTypeStep && !isNTierTemplateStep && !isMonitoringTemplateOnly
            ? selectionFooter
            : null}
        </div>
      </SidePanel>
      <Modal
        open={isResetConfirmOpen}
        title={t('valueChainMap.aiPanelResetAllTitle')}
        description={t('valueChainMap.aiPanelResetAllDescription')}
        closeTitle={t('common.cancel')}
        confirmTitle={t('common.resetAll')}
        onReject={() => setIsResetConfirmOpen(false)}
        onConfirm={handleResetConfirm}
      />
    </>
  );
}

export default AICardPanel;
